import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Loader2, Plus, FileText, X, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const L = {
  hu: { title: "Bizonyíték hozzáadása", note: "Megjegyzés (opcionális)", add: "Fájlok hozzáadása", submit: "Bizonyíték beküldése", done: "Bizonyíték hozzáadva a bejelentéshez", uploading: "Feltöltés…" },
  en: { title: "Add evidence", note: "Note (optional)", add: "Add files", submit: "Submit evidence", done: "Evidence added to the report", uploading: "Uploading…" },
  de: { title: "Nachweis hinzufügen", note: "Notiz (optional)", add: "Dateien hinzufügen", submit: "Nachweis senden", done: "Nachweis zur Meldung hinzugefügt", uploading: "Wird hochgeladen…" },
  fr: { title: "Ajouter une preuve", note: "Note (facultatif)", add: "Ajouter des fichiers", submit: "Envoyer la preuve", done: "Preuve ajoutée au signalement", uploading: "Téléversement…" },
  cs: { title: "Přidat důkaz", note: "Poznámka (volitelné)", add: "Přidat soubory", submit: "Odeslat důkaz", done: "Důkaz byl přidán k oznámení", uploading: "Nahrávání…" },
};

export function AddEvidence({ reference }) {
  const { lang, tr } = useLang();
  const t = L[lang] || L.en;
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const addFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((a) => [...a, r.data]);
      } catch {}
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!note.trim() && !files.length) return;
    setSubmitting(true);
    try {
      await api.post(`/reports/${reference}/evidence`, { note: note.trim(), attachments: files });
      toast.success(t.done, { description: reference });
      setNote("");
      setFiles([]);
    } catch { toast.error("Error"); } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-4 rounded-lg border border-zinc-800/70 bg-[#111113] p-4" data-testid="add-evidence">
      <div className="mb-3 text-[13px] font-semibold text-zinc-100">{t.title}</div>
      <div data-testid="csam-warning-evidence" className="mb-3 flex items-start gap-2.5 rounded-lg border border-red-600/50 bg-red-950/40 px-3 py-2.5">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-400" />
        <p className="text-[12px] font-medium leading-relaxed text-red-200">{tr.csamWarning}</p>
      </div>
      <Textarea data-testid="evidence-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.note} className="min-h-[72px] resize-none border-zinc-800 bg-zinc-950 text-[13px]" />
      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {files.map((f) => (
            <span key={f.id} className="inline-flex items-center gap-1 rounded border border-zinc-800 bg-zinc-950 px-1.5 py-0.5 font-mono text-[11px] text-zinc-300">
              <FileText size={11} /> {f.filename}
              <button onClick={() => setFiles((a) => a.filter((x) => x.id !== f.id))} className="text-zinc-500 hover:text-red-400"><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.gif,.webp,.pdf" onChange={addFiles} className="hidden" data-testid="evidence-file-input" />
        <Button type="button" variant="outline" data-testid="evidence-add-files" disabled={uploading} onClick={() => fileRef.current?.click()} className="h-9 border-zinc-800 bg-zinc-900 text-[13px] text-zinc-300 hover:bg-zinc-800">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />} {uploading ? t.uploading : t.add}
        </Button>
        <Button type="button" data-testid="evidence-submit" disabled={submitting || uploading || (!note.trim() && !files.length)} onClick={submit} className="h-9 bg-blue-600 text-[13px] text-white hover:bg-blue-700">
          {submitting ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {t.submit}
        </Button>
      </div>
    </div>
  );
}
